import { DEFAULT_ONBOARDING_STATE, EMPTY_BUSINESS_PROFILE } from './constants';
import type {
  OnboardingBusinessProfile,
  OnboardingGeneratedChecklistResult,
  OnboardingState,
} from './types';

export interface ParsedChecklistCategory {
  name: string;
  questions: string[];
}

export interface ParsedChecklistImport {
  categories: ParsedChecklistCategory[];
}

const HEADING_PATTERNS = [
  /^(?:שלב|קטגוריה|חלק)\s*\d*\s*[:\-–].+$/i,
  /^(?:stage|category|section|part)\s*\d*\s*[:\-–].+$/i,
];

const isChecklistHeading = (line: string): boolean => {
  const normalized = line.trim();
  if (!normalized) {
    return false;
  }

  return HEADING_PATTERNS.some((pattern) => pattern.test(normalized));
};

const normalizeQuestionText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'object' && value !== null && 'text' in value) {
    const text = (value as { text?: unknown }).text;
    return typeof text === 'string' ? text.trim() : '';
  }

  return '';
};

const extractJsonCandidate = (value: string): string | null => {
  const trimmed = value.trim();

  const fencedMatch = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (fencedMatch?.[1]) {
    return fencedMatch[1].trim();
  }

  const firstBrace = trimmed.indexOf('{');
  const lastBrace = trimmed.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    return trimmed.slice(firstBrace, lastBrace + 1).trim();
  }

  const firstBracket = trimmed.indexOf('[');
  const lastBracket = trimmed.lastIndexOf(']');
  if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
    return trimmed.slice(firstBracket, lastBracket + 1).trim();
  }

  return null;
};

const DEFAULT_CATEGORY_NAME = 'AI Playbook';

const buildGeneratedChecklistResult = (
  categories: ParsedChecklistCategory[],
  summaryPrefix = 'יצרנו'
): OnboardingGeneratedChecklistResult => {
  const sanitizedCategories = categories
    .map((category) => ({
      name: category.name.trim() || DEFAULT_CATEGORY_NAME,
      questions: category.questions.map((question) => question.trim()).filter(Boolean),
    }))
    .filter((category) => category.questions.length > 0);

  if (sanitizedCategories.length === 0) {
    throw new Error('No valid checklist categories were returned by the agent');
  }

  const questionCount = sanitizedCategories.reduce((total, category) => total + category.questions.length, 0);

  return {
    categories: sanitizedCategories,
    categoryCount: sanitizedCategories.length,
    questionCount,
    summary: `${summaryPrefix} ${sanitizedCategories.length} קטגוריות ו-${questionCount} שאלות פתיחה להתחלת העבודה של הצוות.`,
  };
};

export const normalizeBusinessProfile = (
  businessProfile?: Partial<OnboardingBusinessProfile> | null
): OnboardingBusinessProfile => ({
  ...EMPTY_BUSINESS_PROFILE,
  ...businessProfile,
});

export const normalizeOnboardingState = (
  onboardingState?: Partial<OnboardingState> | null
): OnboardingState => ({
  ...DEFAULT_ONBOARDING_STATE,
  ...onboardingState,
  completedSteps: Array.from(new Set(onboardingState?.completedSteps ?? [])).sort((a, b) => a - b),
  currentStep: (onboardingState?.currentStep ?? DEFAULT_ONBOARDING_STATE.currentStep) as OnboardingState['currentStep'],
});

export const mergeCompletedSteps = (existingSteps: number[], step: number): number[] =>
  Array.from(new Set([...existingSteps, step])).sort((a, b) => a - b);

export const buildBusinessProfileSummary = (businessProfile: OnboardingBusinessProfile): string => {
  const sections = [
    ['What we do', businessProfile.whatYouDo],
    ['Why customers choose us', businessProfile.whyChooseYou],
    ['Product details', businessProfile.productDetails],
    ['Lead sources and goal', businessProfile.leadSourcesAndGoal],
    ['Common questions and answers', businessProfile.commonQuestionsAndAnswers],
  ];

  return sections
    .filter(([, value]) => value.trim().length > 0)
    .map(([label, value]) => `${label}: ${value.trim()}`)
    .join('\n\n');
};

export const parsePastedChecklistText = (text: string): ParsedChecklistImport | null => {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return null;
  }

  const categories: ParsedChecklistCategory[] = [];
  let currentCategory: ParsedChecklistCategory | null = null;

  for (const line of lines) {
    if (isChecklistHeading(line)) {
      currentCategory = { name: line, questions: [] };
      categories.push(currentCategory);
      continue;
    }

    if (!currentCategory) {
      return null;
    }

    currentCategory.questions.push(line);
  }

  if (categories.length === 0 || categories.some((category) => category.questions.length === 0)) {
    return null;
  }

  return { categories };
};

export const parseGeneratedChecklistResult = (payload: unknown): OnboardingGeneratedChecklistResult => {
  const rawResult = typeof payload === 'object' && payload !== null && 'result' in payload
    ? (payload as { result?: unknown }).result
    : payload;

  let parsedResult: any = rawResult;

  if (typeof rawResult === 'string' && rawResult.trim()) {
    try {
      parsedResult = JSON.parse(rawResult);
    } catch {
      const jsonCandidate = extractJsonCandidate(rawResult);
      if (jsonCandidate && jsonCandidate !== rawResult.trim()) {
        try {
          parsedResult = JSON.parse(jsonCandidate);
        } catch {
          parsedResult = rawResult;
        }
      }
    }
  }

  if (typeof parsedResult === 'string' && parsedResult.trim()) {
    const parsedChecklist = parsePastedChecklistText(parsedResult);
    if (parsedChecklist) {
      return buildGeneratedChecklistResult(parsedChecklist.categories);
    }

    const jsonCandidate = extractJsonCandidate(parsedResult);
    if (jsonCandidate && jsonCandidate !== parsedResult.trim()) {
      try {
        return parseGeneratedChecklistResult(JSON.parse(jsonCandidate));
      } catch {
        // Fall through to line-based fallback below.
      }
    }

    const flatQuestions = parsedResult
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    if (flatQuestions.length > 0) {
      return buildGeneratedChecklistResult([{ name: 'AI Playbook', questions: flatQuestions }], 'יצרנו עבורך');
    }
  }

  if (typeof rawResult === 'string' && rawResult.trim() && typeof parsedResult === 'string') {
    try {
      parsedResult = JSON.parse(rawResult);
    } catch {
      const parsedChecklist = parsePastedChecklistText(rawResult);
      if (parsedChecklist) {
        return buildGeneratedChecklistResult(parsedChecklist.categories);
      }

      const flatQuestions = rawResult
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      if (flatQuestions.length > 0) {
        return buildGeneratedChecklistResult([{ name: 'AI Playbook', questions: flatQuestions }], 'יצרנו עבורך');
      }
    }
  }

  if (typeof parsedResult === 'object' && parsedResult !== null && 'result' in parsedResult) {
    const nestedResult = (parsedResult as { result?: unknown }).result;
    if (nestedResult) {
      return parseGeneratedChecklistResult(nestedResult);
    }
  }

  if (Array.isArray(parsedResult)) {
    const flatQuestions = parsedResult
      .map((question) => normalizeQuestionText(question))
      .filter(Boolean);

    if (flatQuestions.length > 0) {
      return buildGeneratedChecklistResult([{ name: 'AI Playbook', questions: flatQuestions }], 'יצרנו עבורך');
    }
  }

  const candidateCategoryArrays: unknown[][] = [];
  if (Array.isArray(parsedResult?.categories)) {
    candidateCategoryArrays.push(parsedResult.categories);
  }
  if (Array.isArray(parsedResult?.checklist?.categories)) {
    candidateCategoryArrays.push(parsedResult.checklist.categories);
  }
  if (Array.isArray(parsedResult?.data?.categories)) {
    candidateCategoryArrays.push(parsedResult.data.categories);
  }

  for (const rawCategories of candidateCategoryArrays) {
    const categoriesFromPayload = rawCategories
      .map((category: any) => {
        const rawName = typeof category?.name === 'string'
          ? category.name
          : typeof category?.title === 'string'
            ? category.title
            : typeof category?.category === 'string'
              ? category.category
              : '';
        const rawQuestionsSource = Array.isArray(category?.questions)
          ? category.questions
          : Array.isArray(category?.items)
            ? category.items
            : Array.isArray(category?.prompts)
              ? category.prompts
              : [];

        return {
          name: rawName.trim(),
          questions: rawQuestionsSource
            .map((question: unknown) => normalizeQuestionText(question))
            .filter(Boolean),
        };
      })
      .filter((category: { name: string; questions: string[] }) => category.questions.length > 0);

    if (categoriesFromPayload.length > 0) {
      return buildGeneratedChecklistResult(categoriesFromPayload);
    }
  }

  const fallbackQuestionSources: unknown[] = [
    parsedResult?.questions,
    parsedResult?.items,
    parsedResult?.checklist?.questions,
    parsedResult?.data?.questions,
  ];
  for (const source of fallbackQuestionSources) {
    if (Array.isArray(source)) {
      const flat = source.map((question) => normalizeQuestionText(question)).filter(Boolean);
      if (flat.length > 0) {
        return buildGeneratedChecklistResult([{ name: 'AI Playbook', questions: flat }], 'יצרנו עבורך');
      }
    }
  }

  const salvaged = salvageQuestionsFromUnknown(parsedResult);
  if (salvaged.length > 0) {
    return buildGeneratedChecklistResult([{ name: 'AI Playbook', questions: salvaged }], 'יצרנו עבורך');
  }

  throw new Error('No valid checklist categories were returned by the agent');
};

const salvageQuestionsFromUnknown = (value: unknown, depth = 0): string[] => {
  if (depth > 6 || value == null) {
    return [];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    const collected: string[] = [];
    for (const entry of value) {
      const text = normalizeQuestionText(entry);
      if (text) {
        collected.push(text);
        continue;
      }
      collected.push(...salvageQuestionsFromUnknown(entry, depth + 1));
    }
    return collected;
  }

  if (typeof value === 'object') {
    const collected: string[] = [];
    for (const entry of Object.values(value as Record<string, unknown>)) {
      collected.push(...salvageQuestionsFromUnknown(entry, depth + 1));
    }
    return collected;
  }

  return [];
};

export const buildProgressState = (
  currentState: OnboardingState,
  nextStep: OnboardingState['currentStep'],
  completedStep?: number,
  completedByUserId?: string | null
): OnboardingState => ({
  ...currentState,
  currentStep: nextStep,
  completedSteps: completedStep
    ? mergeCompletedSteps(currentState.completedSteps, completedStep)
    : currentState.completedSteps,
  lastSavedAt: new Date().toISOString(),
  completedByUserId: completedByUserId ?? currentState.completedByUserId,
});

export const isRequiredBusinessProfileComplete = (businessProfile: OnboardingBusinessProfile): boolean =>
  Object.values(businessProfile).every((value) => value.trim().length > 0);
